import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { getFinancialDashboardWorks, type FinancialDashboardWorks, type FinancialStatus } from "../../obras/obrasApi";
import { FinancialKpiCard, FinancialStatusBadge, formatCurrency, formatPercent } from "../Obras/components/FinancialDashboardShared";

const STATUS_LABELS: Record<FinancialStatus, string> = {
  NoData: "Sem dados",
  Healthy: "Saudável",
  Attention: "Atenção",
  Critical: "Crítico",
};
const STATUS_COLORS: Record<FinancialStatus, string> = {
  NoData: "#9ca3af",
  Healthy: "#10b981",
  Attention: "#f59e0b",
  Critical: "#ef4444",
};
const STATUS_ORDER: FinancialStatus[] = ["Critical", "Attention", "Healthy", "NoData"];

export function FinancialDashboardPanel() {
  const [dashboard, setDashboard] = useState<FinancialDashboardWorks | null>(null);
  const [onlyCritical, setOnlyCritical] = useState(false);
  const [hasPendingValidation, setHasPendingValidation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getFinancialDashboardWorks({ onlyCritical: onlyCritical || null, hasPendingValidation: hasPendingValidation || null })
      .then(setDashboard)
      .catch((err) => setError(err instanceof Error ? err.message : "Erro ao carregar dashboard financeiro."));
  }, [onlyCritical, hasPendingValidation]);

  const statusDistribution = useMemo(() => {
    if (!dashboard) return [];
    const counts = new Map<FinancialStatus, number>();
    for (const row of dashboard.works) {
      counts.set(row.financialStatus, (counts.get(row.financialStatus) ?? 0) + 1);
    }
    return STATUS_ORDER
      .map((status) => ({ status, name: STATUS_LABELS[status], value: counts.get(status) ?? 0, color: STATUS_COLORS[status] }))
      .filter((slice) => slice.value > 0);
  }, [dashboard]);

  const marginByWork = useMemo(() => {
    if (!dashboard) return [];
    return dashboard.works
      .map((row) => ({
        name: row.workCode,
        fullName: `${row.workCode} · ${row.workName}`,
        margin: row.totals.realMarginAmount,
      }))
      .sort((a, b) => b.margin - a.margin)
      .slice(0, 10);
  }, [dashboard]);

  if (error) return <div className="border border-destructive/30 bg-destructive/10 text-destructive px-4 py-3">{error}</div>;
  if (!dashboard) return <div className="text-secondary-text py-6">A carregar dashboard financeiro…</div>;

  const totals = dashboard.totals;

  return (
    <section className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-foreground" style={{ fontSize: "var(--text-h3)", fontWeight: "var(--font-weight-semi-bold)" }}>Financeiro consolidado</h2>
          <p className="text-secondary-text mt-1">Resumo de obras, receitas, despesas e margem.</p>
        </div>
        <div className="flex gap-2 text-sm">
          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={onlyCritical} onChange={(e) => setOnlyCritical(e.target.checked)} /> Só críticas</label>
          <label className="inline-flex items-center gap-2"><input type="checkbox" checked={hasPendingValidation} onChange={(e) => setHasPendingValidation(e.target.checked)} /> Por validar</label>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <FinancialKpiCard label="Obras activas" value={String(dashboard.activeWorksCount)} />
        <FinancialKpiCard label="Valor orçamentado" value={formatCurrency(totals.budgetedAmount)} />
        <FinancialKpiCard label="Receitas totais" value={formatCurrency(totals.revenueAmount)} />
        <FinancialKpiCard label="Despesas totais" value={formatCurrency(totals.expenseAmount)} />
        <FinancialKpiCard label="Margem consolidada" value={formatCurrency(totals.realMarginAmount)} hint={formatPercent(totals.realMarginPercent)} />
        <FinancialKpiCard label="Valor por validar" value={formatCurrency(totals.pendingValidationAmount)} hint={`${totals.pendingValidationCount} registo(s)`} />
        <FinancialKpiCard label="Obras em risco" value={String(dashboard.riskWorksCount)} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {statusDistribution.length > 0 && (
          <div className="bg-card border border-border" style={{ borderRadius: "var(--radius-card)" }}>
            <div className="px-5 py-4 border-b border-border"><h3 className="text-foreground" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>Estado financeiro das obras</h3></div>
            <div className="p-5">
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={statusDistribution} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={2}>
                    {statusDistribution.map((slice) => <Cell key={slice.status} fill={slice.color} />)}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} obra(s)`, name]} contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--radius)" }} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        {marginByWork.length > 0 && (
          <div className="bg-card border border-border" style={{ borderRadius: "var(--radius-card)" }}>
            <div className="px-5 py-4 border-b border-border"><h3 className="text-foreground" style={{ fontWeight: "var(--font-weight-semi-bold)" }}>Margem por obra</h3></div>
            <div className="p-5">
              <ResponsiveContainer width="100%" height={Math.max(220, marginByWork.length * 36)}>
                <BarChart data={marginByWork} layout="vertical" margin={{ top: 4, right: 24, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 12 }} stroke="var(--secondary-text)" />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} stroke="var(--secondary-text)" />
                  <Tooltip formatter={(value) => formatCurrency(Number(value))} labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName ?? ""} contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--radius)" }} />
                  <Bar dataKey="margin" name="Margem" radius={[0, 4, 4, 0]}>
                    {marginByWork.map((row) => <Cell key={row.name} fill={row.margin >= 0 ? "#10b981" : "#ef4444"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
      <div className="bg-card border border-border overflow-x-auto" style={{ borderRadius: "var(--radius-card)" }}>
        <table className="min-w-[1100px] w-full"><thead><tr className="border-b border-border bg-muted text-left"><th className="px-4 py-3">Obra</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Estado obra</th><th className="px-4 py-3 text-right">Orçamentado</th><th className="px-4 py-3 text-right">Receitas</th><th className="px-4 py-3 text-right">Despesas</th><th className="px-4 py-3 text-right">Margem</th><th className="px-4 py-3 text-right">Desvio</th><th className="px-4 py-3">Estado financeiro</th><th className="px-4 py-3">Por validar</th></tr></thead><tbody>{dashboard.works.length === 0 ? <tr><td colSpan={10} className="px-4 py-6 text-center text-secondary-text">Sem obras para os filtros seleccionados.</td></tr> : dashboard.works.map((row) => <tr key={row.workId} className="border-b border-border last:border-b-0"><td className="px-4 py-3"><a className="text-primary underline" href={`/obras/${row.workId}`}>{row.workCode} · {row.workName}</a></td><td className="px-4 py-3">{row.clientName}</td><td className="px-4 py-3">{row.workStatus || "—"}</td><td className="px-4 py-3 text-right">{formatCurrency(row.totals.budgetedAmount)}</td><td className="px-4 py-3 text-right">{formatCurrency(row.totals.revenueAmount)}</td><td className="px-4 py-3 text-right">{formatCurrency(row.totals.expenseAmount)}</td><td className="px-4 py-3 text-right">{formatCurrency(row.totals.realMarginAmount)} <span className="text-secondary-text">({formatPercent(row.totals.realMarginPercent)})</span></td><td className="px-4 py-3 text-right">{formatCurrency(row.totals.revenueDeviationAmount)}</td><td className="px-4 py-3"><FinancialStatusBadge status={row.financialStatus} /></td><td className="px-4 py-3">{row.totals.pendingValidationCount > 0 ? `${formatCurrency(row.totals.pendingValidationAmount)} (${row.totals.pendingValidationCount})` : "—"}</td></tr>)}</tbody></table>
      </div>
    </section>
  );
}
